/* eslint-disable indent */
import { afterNextRender, Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import linkifyHtml from 'linkify-html';
import { DateTime } from 'luxon';
import { catchError, EMPTY, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { MAX_FILE_SIZE_LIMIT } from '../../constants/general.constant';
import { IMenuAction, MENU_ACTIONS } from '../../constants/menuAction.constant';
import { getPostTypeLabel, PostOwnershipEnum, PostTypeEnum } from '../../constants/post.constant';
import { PrivacyTypeEnum } from '../../constants/privacy.constant';
import { BadgePopupComponent } from '../../libraries/popup/components/badge-popup/badge-popup.component';
import { BibleVersePopupComponent } from '../../libraries/popup/components/bible-verse-popup/bible-verse-popup.component';
import { ConfirmationPopupComponent } from '../../libraries/popup/components/confirmation-popup/confirmation-popup.component';
import { CreatePostPopupComponent } from '../../libraries/popup/components/create-post-popup/create-post-popup.component';
import { ReportPostPopupComponent } from '../../libraries/popup/components/report-post-popup/report-post-popup.component';
import { SharePostPopupComponent } from '../../libraries/popup/components/share-post-popup/share-post-popup.component';
import { SuccessPopupComponent } from '../../libraries/popup/components/success-popup/success-popup.component';
import { TestimonyPopupComponent } from '../../libraries/popup/components/testimony-popup/testimony-popup.component';
import { IBibleVerseModel } from '../../models/bible.model';
import { IURLPreviewModel } from '../../models/general.model';
import {
    ICreatePostCommentModel,
    IOptimisticPostComment,
    IPostCommentModel,
    IPostModel,
} from '../../models/post.model';
import { IUploadPhotoAPIResponse } from '../../models/upload.model';
import { GeneralService } from '../../services/api/general/general.service';
import { GroupsService } from '../../services/api/groups/groups.service';
import { PostCreationService } from '../../services/api/post/post-creation.service';
import { PostService } from '../../services/api/post/post.service';
import { UploadService } from '../../services/api/upload/upload.service';
import { UserService } from '../../services/api/user/user.service';
import { CreatePostEventService } from '../../services/helper/create-post-event/create-post-event.service';
import { LoaderService } from '../../services/loader/loader.service';
import { StorageService } from '../../services/storage/storage.service';

@Component({
    selector: 'app-post',
    standalone: false,
    templateUrl: './post.component.html',
    styleUrl: './post.component.scss',
})
export class PostComponent implements OnInit {
    @Input() isShowActions = true;
    @Input() isShowComment = false;
    @Input() isPinned = false;
    @Input() hasPendingPostComment!: WritableSignal<boolean>;
    protected postSignal = signal<IPostModel>({} as IPostModel);
    protected postCommentSignal = signal<IPostCommentModel[]>([] as IPostCommentModel[]);
    protected PrivacyTypeEnum = PrivacyTypeEnum;
    protected PostTypeEnum = PostTypeEnum;
    protected getPostTypeLabel = getPostTypeLabel;
    protected coloredContent!: SafeHtml;
    protected coloredQuoteContent!: SafeHtml;
    protected urlPreview = signal<IURLPreviewModel | null>(null);
    protected quoteUrlPreview = signal<IURLPreviewModel | null>(null);
    protected getUserProfileDetails = this.storageService.getLoginUser();
    protected isOwnPost = signal<boolean>(false);
    protected isOwnQuotePost = signal<boolean>(false);
    protected menuActions = signal<IMenuAction[]>([]);
    protected MENU_ACTIONS = MENU_ACTIONS;
    protected commentImage?: File;
    protected commentImageUrl?: string;
    protected selectedBibleVerses = signal<IBibleVerseModel[]>([]);
    protected commentForm = this.fb.group({
        content: ['', Validators.required],
        attachment: [null as number | null],
        verses: [[] as number[]],
        url: [''],
    });
    protected commentPayload = signal<ICreatePostCommentModel>({} as ICreatePostCommentModel);
    protected isEditingCommentSignal = signal<boolean>(false);
    protected editingCommentIdSignal = signal<number | null>(null);

    private savedListId!: number;

    constructor(
        private createPostEventService: CreatePostEventService,
        private dialog: MatDialog,
        private fb: NonNullableFormBuilder,
        private generalService: GeneralService,
        private groupsService: GroupsService,
        private loaderService: LoaderService,
        private postService: PostService,
        private router: Router,
        private sanitizer: DomSanitizer,
        private storageService: StorageService,
        private uploadService: UploadService,
        private userService: UserService,
        private postCreationService: PostCreationService,
    ) {
        afterNextRender(() => {
            this.extractThumbnail();
            if (this.isShowComment) {
                this.loadComments();
            }
            this.updateMenuActions();
            this.createPostEventService.badgeSent$.subscribe((postId) => {
                if (postId === this.postSignal().id) {
                    this.postSignal.update((p) => ({
                        ...p,
                        badgeCount: p.badgeCount + 1,
                    })); // increase badge count when badge sent
                }
            });
            this.createPostEventService.postCommentCreated$.subscribe(() => {
                this.postSignal.update((p) => ({
                    ...p,
                    replyCount: ++p.replyCount,
                })); // increase reply count when comment created
            });
        });
    }

    @Input() set post(value: IPostModel) {
        this.postSignal.set(value);
        this.isOwnPost.set(value.author.id === this.getUserProfileDetails?.id);
        this.isOwnQuotePost.set(value?.originator?.author.id === this.getUserProfileDetails?.id);
    }

    ngOnInit(): void {
        this.coloredContent = this.highlightHashtagsAndLinks(this.postSignal().content);
        this.coloredQuoteContent = this.highlightHashtagsAndLinks(this.postSignal()?.originator?.content);
    }

    protected cancelEditComment(): void {
        this.isEditingCommentSignal.set(false);
        this.editingCommentIdSignal.set(null);
        this.commentForm.reset();
        this.removeCommentImage();
        this.removeBibleVerse();
    }

    protected getInitials(name: string | undefined): string {
        if (!name) {
            return '';
        }

        const parts = name.trim().split(' ');
        if (parts.length === 1) {
            return parts[0].charAt(0).toUpperCase();
        }

        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    protected onEditComment(comment: IPostCommentModel): void {
        this.isEditingCommentSignal.set(true);
        this.editingCommentIdSignal.set(comment.id);
        this.commentForm.patchValue({
            content: comment.content,
            attachment: comment.attachmentId || null,
            verses: comment.verses?.map((v) => v.id) || [],
        });
        this.commentImageUrl = comment.attachment || '';
        this.selectedBibleVerses.set(comment.verses || []);

        setTimeout(() => {
            const element = document.getElementById('comment-form');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    }

    protected timeAgo(time: string): string | null {
        const now = DateTime.now();
        const postTime = DateTime.fromISO(time);
        const minutes = now.diff(postTime, 'minutes').minutes;
        const hours = now.diff(postTime, 'hours').hours;
        const days = now.diff(postTime, 'days').days;

        if (minutes < 1) {
            return 'Just now';
        }
        if (minutes < 60) {
            return `${Math.floor(minutes)}m ago`;
        }
        if (hours < 24) {
            return `${Math.floor(hours)}h ago`;
        }
        return `${Math.floor(days)}d ago`;
    }

    protected executeMenuAction(actionId: string): void {
        switch (actionId) {
            case 'save':
                this.savePost();
                break;
            case 'delete':
                this.openDeletePostPopup();
                break;
            case 'quote':
                this.openQuotePostPopup();
                break;
            case 'share':
                this.openSharePostPopup();
                break;
            case 'edit':
                this.openEditPostPopup();
                break;
            case 'hide':
                this.openHidePostPopup();
                break;
            case 'report':
                this.openReportPostPopup();
                break;
            case 'pin':
                this.togglePinGroupPost(true);
                break;
            case 'unpin':
                this.togglePinGroupPost(false);
                break;
            case 'mark-answered':
                this.openMarkAsAnsweredPopup();
                break;
            case 'reply':
                this.router.navigate(['post', this.postSignal().id]);
                break;
            case 'block':
                this.openBlockUserPopup();
                break;
            default:
                break;
        }
    }

    protected getVerseReference(verses: IBibleVerseModel[]): string {
        if (!verses) {
            return '';
        }

        const first = verses[0];
        const last = verses[verses.length - 1];

        const bookType = first.type;
        const chapter = first.chapter;

        // Handle single verse or a range
        const versePart = verses.length === 1 ? `${first.list}` : `${first.list}-${last.list}`;

        return `${bookType} ${chapter}:${versePart}`;
    }

    protected openBadgePopup(): void {
        const popupConfig = BadgePopupComponent.getDialogConfig();
        popupConfig.data = this.postSignal();
        this.dialog.open(BadgePopupComponent, popupConfig);
    }

    protected openBlockUserPopup(): void {
        const popupConfig = ConfirmationPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: 'Block User',
            message: 'Are you sure? They will no longer be able to connect with you.',
            iconPath: '/assets/images/shared/block-user-grey-icon.svg',
            confirmButtonLabel: 'Block',
        };
        const dialogRef = this.dialog.open(ConfirmationPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe({
            next: (result: boolean) => {
                if (!result) {
                    return;
                }

                this.loaderService.show();
                this.userService.blockUser(this.postSignal()?.author.id).subscribe({
                    next: () => {
                        this.createPostEventService.notifyPostCreated();
                    },
                    error: (error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error blocking:', error);
                        this.loaderService.hide();
                        return EMPTY;
                    },
                    complete: () => {
                        this.loaderService.hide();
                    },
                });
            },
        });
    }

    protected openSharePostPopup(): void {
        const popupConfig = SharePostPopupComponent.getDialogConfig();
        popupConfig.data = this.postSignal();
        this.dialog.open(SharePostPopupComponent, popupConfig);
    }

    protected openEditPostPopup(): void {
        const popupConfig = CreatePostPopupComponent.getDialogConfig();
        popupConfig.maxWidth = '768px';
        popupConfig.data = { post: this.postSignal(), isEdit: true };
        this.dialog.open(CreatePostPopupComponent, popupConfig);
    }

    protected openQuotePostPopup(): void {
        const popupConfig = CreatePostPopupComponent.getDialogConfig();
        popupConfig.maxWidth = '768px';
        popupConfig.data = {
            quote: this.postSignal(),
            quoteUrlPreview: this.quoteUrlPreview,
            isQuote: true,
        };
        this.dialog.open(CreatePostPopupComponent, popupConfig);
    }

    protected openHidePostPopup(): void {
        const popupConfig = ConfirmationPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: 'Hide this post?',
            message: "We won't show this in your feed anymore.",
            iconPath: '/assets/images/shared/hide-grey-icon.svg',
            confirmButtonLabel: 'Hide',
        };
        const dialogRef = this.dialog.open(ConfirmationPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe({
            next: (result: boolean) => {
                if (!result) {
                    return;
                }

                this.loaderService.show();
                this.postService.hidePost(this.postSignal().id).subscribe({
                    next: () => {
                        this.createPostEventService.notifyPostDeleted();
                        this.createPostEventService.notifyPostCreated();
                    },
                    error: (error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error hiding post:', error);
                        return EMPTY;
                    },
                    complete: () => {
                        this.loaderService.hide();
                    },
                });
            },
            error: (error) => {
                console.error('Error closing hide post popup:', error);
            },
        });
    }

    protected openReportPostPopup(): void {
        const popupConfig = ReportPostPopupComponent.getDialogConfig();
        popupConfig.data = this.postSignal();
        const dialogRef = this.dialog.open(ReportPostPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe({
            next: (result: boolean) => {
                if (!result) {
                    return;
                }

                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: 'Thanks for looking out.',
                    message: "We appreciate you helping keep our community safe. We'll review this shortly.",
                    iconPath: '/assets/images/shared/shield-icon.svg',
                };
                this.dialog.open(SuccessPopupComponent, popupConfig);
            },
            error: (error) => {
                console.error('Error closing report post popup:', error);
            },
        });
    }

    protected openMarkAsAnsweredPopup(): void {
        const popupConfig = ConfirmationPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: 'Praise God!',
            message: 'Since this prayer has been answered, would you like to add a testimony??',
            iconPath: '/assets/images/shared/success-green-icon.svg',
            confirmButtonLabel: 'Yes, Share!',
            cancelButtonLabel: 'No',
        };

        const dialogRef = this.dialog.open(ConfirmationPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this.openTestimonyPopup();
            } else {
                this.postService.markPostAnswered(this.postSignal().id, null).subscribe({
                    next: () => {
                        this.postSignal.update((p) => ({
                            ...p,
                            answered: true,
                        }));
                    },
                    error: (error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error marking post as answered:', error);
                        return EMPTY;
                    },
                    complete: () => {
                        this.loaderService.hide();
                    },
                });
            }
        });
    }

    protected openTestimonyPopup(): void {
        const popupConfig = TestimonyPopupComponent.getDialogConfig();
        popupConfig.maxWidth = '768px';
        popupConfig.data = { postId: this.postSignal().id };
        const dialogRef = this.dialog.open(TestimonyPopupComponent, popupConfig);

        dialogRef.afterClosed().subscribe((testimony: string | null) => {
            if (testimony === '' || testimony === null) {
                return;
            } else {
                this.postService.markPostAnswered(this.postSignal().id, testimony).subscribe({
                    next: () => {
                        this.postSignal.update((p) => ({
                            ...p,
                            answered: true,
                        }));
                    },
                    error: (error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error marking post as answered:', error);
                        return EMPTY;
                    },
                    complete: () => {
                        this.loaderService.hide();
                    },
                });
            }
        });
    }

    protected openDeletePostPopup(): void {
        const popupConfig = ConfirmationPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: 'Delete Post?',
            message: 'Are you sure? This will permanently remove this post from the community feed.',
            iconPath: '/assets/images/shared/delete-red-icon.svg',
            confirmButtonLabel: 'Yes, Delete',
            cancelButtonLabel: 'Keep',
        };

        const dialogRef = this.dialog.open(ConfirmationPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (!result) {
                return;
            } else {
                this.postService.deletePost(this.postSignal().id).subscribe({
                    next: () => {
                        this.createPostEventService.notifyPostDeleted();
                        const popupConfig = SuccessPopupComponent.getDialogConfig();
                        popupConfig.data = {
                            title: 'Post Removed',
                            message: 'You have successfully deleted this post.',
                            iconPath: '/assets/images/shared/success-green-icon.svg',
                        };
                        this.dialog.open(SuccessPopupComponent, popupConfig);
                    },
                    error: (error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error deleting post:', error);
                        return EMPTY;
                    },
                    complete: () => {
                        this.loaderService.hide();
                    },
                });
            }
        });
    }

    protected toggleLike(post: IPostModel): void {
        const isLiked = post.liked;
        const previousLikeCount = post.likeCount;

        this.postSignal.update((p) => ({
            ...p,
            liked: !isLiked,
            likeCount: isLiked ? Math.max(0, p.likeCount - 1) : p.likeCount + 1,
        }));

        const request$ = isLiked ? this.postService.unlikePost(post.id) : this.postService.likePost(post.id);

        request$.subscribe({
            error: (error) => {
                this.postSignal.update((p) => ({
                    ...p,
                    liked: isLiked,
                    likeCount: previousLikeCount,
                }));

                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: 'Error',
                    message: error.error?.message ?? 'Something went wrong',
                };

                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Like toggle failed', error);
            },
        });
    }

    protected togglePinGroupPost(pin: boolean): void {
        const post = this.postSignal();
        (pin
            ? this.groupsService.pinGroupPost(post.group.id, post.id)
            : this.groupsService.unpinGroupPost(post.group.id, post.id)
        ).subscribe({
            next: () => {
                this.createPostEventService.notifyGroupPostCreated();
                this.createPostEventService.notifyGroupPinnedPostCreated();
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error(pin ? 'Pin failed' : 'Unpin failed', error);
            },
        });
    }

    protected onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) {
            return;
        }
        if (file.size > MAX_FILE_SIZE_LIMIT) {
            const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
            errorPopupConfig.data = {
                title: `Error`,
                message: `File size must not exceed 10 MB.`,
            };
            this.dialog.open(SuccessPopupComponent, errorPopupConfig);
            input.value = '';
            return;
        }
        this.commentImage = file;
        this.commentImageUrl = URL.createObjectURL(file);
    }

    protected removeCommentImage(): void {
        this.commentImage = undefined;
        this.commentImageUrl = '';
        this.commentForm.patchValue({ attachment: null });
    }

    protected removeBibleVerse(): void {
        this.selectedBibleVerses.set([]);
        this.commentForm.patchValue({ verses: [] });
    }

    protected getSelectedVerseReference(): string {
        if (this.selectedBibleVerses().length === 0) {
            return '';
        }

        const first = this.selectedBibleVerses()[0];
        const last = this.selectedBibleVerses()[this.selectedBibleVerses().length - 1];

        const bookType = first.type;
        const chapter = first.chapter;

        // Handle single verse or a range
        const versePart = this.selectedBibleVerses().length === 1 ? `${first.list}` : `${first.list}-${last.list}`;

        return `${bookType} ${chapter}:${versePart}`;
    }

    protected openBibleVersePopup(): void {
        const popupConfig = BibleVersePopupComponent.getDialogConfig();
        popupConfig.maxHeight = '70vh';
        const dialogRef = this.dialog.open(BibleVersePopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe((result: IBibleVerseModel[]) => {
            if (result) {
                this.selectedBibleVerses.set(result);
                this.commentForm.patchValue({ verses: result.map((item) => item.id) });
            }
        });
    }

    protected submitComment(): void {
        if (this.commentForm.invalid) {
            return;
        }
        const tempId = Date.now();
        const payload = {
            ...this.commentForm.value,
            selectedVerses: this.selectedBibleVerses(),
            photoUrl: this.commentImageUrl,
            photoFile: this.commentImage,
        };

        this.commentPayload.set(payload as ICreatePostCommentModel);
        const optimisticComment = this.postCreationService.createOptimisticComment(this.commentPayload(), tempId);

        this.postCommentSignal.update((prev) => [...prev, optimisticComment as IPostCommentModel]);
        this.commentForm.reset();
        this.removeCommentImage();
        this.removeBibleVerse();

        const photoUpload$: Observable<IUploadPhotoAPIResponse | null> = payload.photoFile
            ? this.uploadCommentImage()
            : of(null);

        photoUpload$
            .pipe(
                switchMap(() => {
                    if (this.isEditingCommentSignal() && this.editingCommentIdSignal()) {
                        return this.editComment();
                    } else {
                        return this.createComment(tempId);
                    }
                }),
                tap((response) => {
                    if (this.isEditingCommentSignal()) {
                        this.postCommentSignal.update((comments) =>
                            comments.map((c) =>
                                c.id === this.editingCommentIdSignal()
                                    ? {
                                          ...c,
                                          content: response.content,
                                          attachment: response.attachment,
                                          verses: response.verses,
                                      }
                                    : c,
                            ),
                        );
                        this.createPostEventService.notifyPostCreated();
                        this.cancelEditComment();
                    } else {
                        this.createPostEventService.notifyPostCommentCreated();
                    }
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    private updateMenuActions(): void {
        const post = this.postSignal();
        const isOwn = this.isOwnPost();
        const visibleActions = this.MENU_ACTIONS.filter((action) => {
            // Check post answered
            if (action.id === 'mark-answered' && post.answered) {
                return false;
            }

            // Check post saved
            if (action.id === 'save' && post.saved) {
                return false;
            }

            // Handle Pin / Unpin logic first
            if (action.id === 'pin' && this.isPinned) {
                return false;
            }
            if (action.id === 'unpin' && !this.isPinned) {
                return false;
            }

            // Check ownership
            if (action.showFor === PostOwnershipEnum.OWN && !isOwn) {
                return false;
            }
            if (action.showFor === PostOwnershipEnum.COMMUNITY && isOwn) {
                return false;
            }

            // Check post types
            if (action.postTypes && !action.postTypes.includes(post.type)) {
                return false;
            }
            if (action.excludeTypes && action.excludeTypes.includes(post.type)) {
                return false;
            }
            if (action.privacyTypes && !action.privacyTypes.includes(post.privacy)) {
                return false;
            }

            return true;
        });

        this.menuActions.set(visibleActions);
    }

    private uploadCommentImage(): Observable<IUploadPhotoAPIResponse> {
        const formData = new FormData();
        formData.append('file', this.commentPayload().photoFile as File);

        return this.uploadService.uploadPhoto(formData).pipe(
            tap((res) => {
                this.commentPayload.update((payload) => {
                    return {
                        ...payload,
                        attachment: res.id,
                    };
                });
            }),
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on upload photo', error);
                return EMPTY;
            }),
        );
    }

    private createComment(tempId: number): Observable<IPostCommentModel> {
        this.hasPendingPostComment.set(true);

        return this.postService.createPostComment(this.postSignal().id, this.commentPayload()).pipe(
            tap((response) => {
                this.replaceOptimisticPostComment(response, tempId);
            }),
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: 'Error',
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on create comment', error);
                return EMPTY;
            }),
            finalize(() => this.hasPendingPostComment.set(false)),
        );
    }

    private editComment(): Observable<IPostCommentModel> {
        return this.postService.editComment(this.editingCommentIdSignal()!, this.commentPayload()).pipe(
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on edit comment', error);
                return EMPTY;
            }),
        );
    }

    private highlightHashtagsAndLinks(text: string): SafeHtml {
        if (!text) {
            return '';
        }

        // Escape unsafe HTML just in case
        let formatted = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');

        // Replace newlines with <br>
        formatted = formatted.replace(/\n/g, '<br>');

        // Highlight hashtags (make them blue)
        formatted = formatted.replace(/(#\w+)/g, `<span style="color: #4F66FF;">$1</span>`);

        // Use linkify to make URLs clickable
        formatted = linkifyHtml(formatted, {
            target: '_blank',
            rel: 'noopener noreferrer',
            className: 'text-[#4F66FF] underline',
        });

        // Mark as safe HTML for Angular
        return this.sanitizer.bypassSecurityTrustHtml(formatted);
    }

    private extractThumbnail(): void {
        if (this.postSignal().url) {
            this.generalService
                .getURLPreview(this.postSignal().url as string)
                .pipe(
                    tap((res) => {
                        if (res) {
                            this.urlPreview.set(res);
                        } else {
                            this.urlPreview.set(null);
                        }
                    }),
                    catchError((error) => {
                        console.error('Error fetching thumbnail preview:', error);
                        this.urlPreview.set(null);
                        return EMPTY;
                    }),
                )
                .subscribe();
        }

        if (this.postSignal()?.originator?.url) {
            this.generalService
                .getURLPreview(this.postSignal().originator.url as string)
                .pipe(
                    tap((res) => {
                        if (res) {
                            this.quoteUrlPreview.set(res);
                        } else {
                            this.quoteUrlPreview.set(null);
                        }
                    }),
                    catchError((error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error fetching thumbnail preview:', error);
                        this.quoteUrlPreview.set(null);
                        return EMPTY;
                    }),
                )
                .subscribe();
        }
    }

    private loadComments(): void {
        this.postService.getPostComments(this.postSignal().id).subscribe({
            next: (response) => {
                this.postCommentSignal.set(response);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error getting post comments:', error);
            },
        });
    }

    private savePost(): void {
        this.postService
            .getSaveListing()
            .pipe(
                tap((list) => {
                    const defaultList = list.find((x) => x.type === 'default');
                    this.savedListId = Number(defaultList?.id);
                    if (!this.savedListId) {
                        console.error('Default saved list not found');
                    }
                }),
                switchMap(() => this.postService.savePost(this.postSignal().id, this.savedListId)),
                tap(() => {
                    this.postSignal.update((p) => ({
                        ...p,
                        saved: true,
                    }));
                    this.updateMenuActions();
                    const popupConfig = SuccessPopupComponent.getDialogConfig();
                    popupConfig.data = {
                        title: `Saved for later.`,
                        message: "We've kept this post safe in your saved items.",
                        iconPath: '/assets/images/shared/success-green-icon.svg',
                    };
                    this.dialog.open(SuccessPopupComponent, popupConfig);
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error loading saved posts', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    private replaceOptimisticPostComment(realComment: IPostCommentModel, tempId: number): void {
        this.postCommentSignal.update((comments) =>
            comments.map((comment) =>
                (comment as IOptimisticPostComment).tempId === `temp-${tempId}` ? realComment : comment,
            ),
        );
    }
}
